import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0 });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/cart`);
      setCart(response.data);
    } catch (error) {
      logger.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (itemId, itemType, quantity = 1, tokenAmount = 0, size = null) => {
    if (!isAuthenticated) {
      throw new Error('Please login to add items to cart');
    }

    try {
      await axios.post(`${API_URL}/cart/add`, {
        item_id: itemId,
        item_type: itemType,
        quantity,
        token_amount: tokenAmount,
        size
      });
      await fetchCart();
    } catch (error) {
      logger.error('Failed to add to cart:', error);
      throw error;
    }
  }, [isAuthenticated, fetchCart]);

  const removeFromCart = useCallback(async (itemId) => {
    try {
      await axios.delete(`${API_URL}/cart/${itemId}`);
      await fetchCart();
    } catch (error) {
      logger.error('Failed to remove from cart:', error);
      throw error;
    }
  }, [fetchCart]);

  const clearCart = useCallback(async () => {
    try {
      await axios.delete(`${API_URL}/cart`);
      await fetchCart();
    } catch (error) {
      logger.error('Failed to clear cart:', error);
      throw error;
    }
  }, [fetchCart]);

  const itemCount = useMemo(() => cart.items.reduce((acc, item) => {
    if (item.item_type === 'token') {
      return acc + 1;
    }
    return acc + (item.quantity || 1);
  }, 0), [cart.items]);

  const contextValue = useMemo(() => ({
    cart,
    loading,
    addToCart,
    removeFromCart,
    clearCart,
    fetchCart,
    itemCount
  }), [cart, loading, addToCart, removeFromCart, clearCart, fetchCart, itemCount]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
